import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock, Sparkles, BookOpen, Dumbbell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DimensionBadge, LevelBadge, DIM_STYLE } from "@/components/Badges";
import { ShareArchetype } from "@/components/ShareArchetype";
import { DIM_WHY, LEVEL_EXPECT } from "@/lib/archetypeCopy";
import { goldBurst } from "@/lib/confetti";
import { Loader } from "@/components/Loader";
import { TopLogoBar } from "@/components/AppShell";
import { TextSizeToggle } from "@/components/TextSizeToggle";

const DIM_LABEL = { direzione: "Direzione", continuita: "Continuità", spazio: "Spazio", ricarica: "Ricarica", motivazione: "Motivazione", mentalita: "Mentalità" };

export default function Report() {
  const { profileId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const stored = (() => {
    try {
      return location.state || JSON.parse(localStorage.getItem("a2g_result") || "null");
    } catch {
      return null;
    }
  })();
  const scores = stored?.profile_id === profileId ? stored?.scores : null;

  useEffect(() => {
    api.get(`/profiles/${profileId}`).then(({ data }) => { setPreview(data); setTimeout(goldBurst, 400); }).catch(() => navigate("/"));
    if (user) api.post("/me/result", { profile_id: profileId, scores }).catch(() => {});
  }, [profileId, user]); // eslint-disable-line

  if (!preview) return <Loader label="Preparo il tuo report…" />;

  const accent = DIM_STYLE[preview.dimension]?.c || "#D4AF37";
  const DimIcon = DIM_STYLE[preview.dimension]?.icon;
  const arch = preview.archetype;
  const maxScore = 15;

  const goUnlock = () => navigate(user?.subscription_active ? "/oggi" : "/paywall");

  return (
    <div className="min-h-screen pb-28" data-testid="report-page">
      <div className="px-5 pt-6">
        <TopLogoBar onBack={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate(user ? "/oggi" : "/"))} testid="report-home-button" right={<TextSizeToggle />} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mx-5 mt-4 overflow-hidden rounded-3xl border p-6"
        style={{ borderColor: `${accent}44`, background: `linear-gradient(135deg, ${accent}16, #0D0D0F)` }}
      >
        <div className="flex items-center gap-2">
          <DimensionBadge dimension={preview.dimension} title={preview.dimension_title} />
          <LevelBadge level={preview.level} title={preview.level_title} />
        </div>
        {DimIcon && (
          <div style={{ borderColor: `${accent}55`, background: `${accent}1A`, color: accent }} className="mt-5 flex h-16 w-16 items-center justify-center rounded-2xl border" data-testid="archetype-medallion">
            <DimIcon size={32} strokeWidth={1.8} />
          </div>
        )}
        <p className="eyebrow mt-4" style={{ color: accent }}>Il tuo archetipo</p>
        <h1 className="font-serif-el mt-1 text-3xl font-semibold leading-tight text-[#FAF9F6]" data-testid="archetype-title">{arch.title}</h1>
        <p className="a2g-read mt-1 text-lg text-[#D4D4D8] font-semibold">{arch.subtitle}</p>
        <p className="mt-4 a2g-read text-[15px] leading-relaxed text-[#B4B4BC]">{arch.description}</p>
        <div className="mt-5">
          <ShareArchetype arch={arch} dimension={preview.dimension} level={preview.level} scores={scores || user?.assessment?.scores} />
        </div>
      </motion.div>

      {scores && (
        <section className="mx-5 mt-6 rounded-3xl border border-[#27272A] bg-[#121215] p-5">
          <p className="eyebrow">Le tue 6 aree</p>
          <div className="mt-4 space-y-3">
            {Object.entries(scores).map(([k, v]) => {
              const c = DIM_STYLE[k]?.c;
              const weakest = k === preview.dimension;
              return (
                <div key={k}>
                  <div className="mb-1 flex items-center justify-between text-base">
                    <span className="font-medium text-[#F4F4F5]">
                      {DIM_LABEL[k]} {weakest && <span className="text-sm text-[#D4AF37]">· focus</span>}
                    </span>
                    <span className="tabular-nums font-semibold text-[#B4B4BC]">{v}/{maxScore}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-[#27272A]">
                    <motion.div className="h-full rounded-full" style={{ backgroundColor: c }} initial={{ width: 0 }} animate={{ width: `${(v / maxScore) * 100}%` }} transition={{ duration: 0.6 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="mx-5 mt-6 rounded-3xl border border-[#27272A] bg-[#121215] p-5">
        <p className="a2g-read text-base leading-relaxed text-[#B4B4BC]">{preview.profilo}</p>
        <div className="mt-4 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/8 p-4">
          <p className="text-[12px] font-semibold uppercase tracking-wider text-[#D4AF37]">Obiettivo della settimana</p>
          <p className="mt-1 a2g-read text-[15px] font-medium text-[#F4F4F5]">{preview.obiettivo}</p>
        </div>
      </section>

      <section className="mx-5 mt-6 space-y-3">
        <div className="rounded-3xl border border-[#27272A] bg-[#121215] p-5" data-testid="report-why">
          <p className="eyebrow" style={{ color: accent }}>Perché è il tuo focus</p>
          <p className="mt-3 a2g-read text-[15px] leading-relaxed text-[#B4B4BC]">{DIM_WHY[preview.dimension]}</p>
        </div>
        <div className="rounded-3xl border border-[#27272A] bg-[#121215] p-5" data-testid="report-expect">
          <p className="eyebrow" style={{ color: accent }}>Cosa aspettarti nei 7 giorni</p>
          <p className="mt-3 a2g-read text-[15px] leading-relaxed text-[#B4B4BC]">{LEVEL_EXPECT[preview.level]}</p>
        </div>
      </section>

      <section className="mx-5 mt-6">
        <p className="eyebrow">Il percorso di 7 giorni</p>
        <div className="mt-4 space-y-2.5">
          {preview.days.map((d) => (
            <div key={d.day} className="flex items-center gap-3 rounded-2xl border border-[#27272A] bg-[#121215] p-4">
              <span className="tabular-nums font-semibold flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#18181C] text-base font-semibold text-[#B4B4BC]">{d.day}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base text-[#F4F4F5] font-semibold">{d.title}</p>
                <div className="mt-0.5 flex gap-3 text-[12px] text-[#8A8E96]">
                  {d.has_lettura && <span className="inline-flex items-center gap-1"><BookOpen size={11} /> Lettura</span>}
                  {d.has_esercizio && <span className="inline-flex items-center gap-1"><Dumbbell size={11} /> Esercizio</span>}
                </div>
              </div>
              {d.day > 1 && <Lock size={16} className="shrink-0 text-[#8A8E96]" />}
            </div>
          ))}
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 mx-auto max-w-[430px] border-t border-[#27272A] bg-[#09090B]/95 p-4 backdrop-blur-md">
        <button onClick={goUnlock} data-testid="unlock-path-button" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base uppercase tracking-wide">
          <Sparkles size={18} /> Sblocca il percorso · 7 giorni gratis <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
